import { useState } from "react"

import InputError from "@/components/input-error"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { Account } from "@/types"

interface UserFormProps {
  data: {
    user: {
      name: string
      avatar: File | null
      email: string
      state: "active" | "inactive" | "invited"
      account_uid: string
      send_welcome_email?: string
    }
  }
  setData: (key: string, value: any) => void
  errors: Record<string, any>
  avatarUrl: string | null
  accounts: Account[]
  mode: "create" | "edit"
}

export default function UserForm({
  data,
  setData,
  errors,
  avatarUrl,
  accounts,
  mode,
}: UserFormProps) {
  const [preview, setPreview] = useState<string | null>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      setData("user", { ...data.user, avatar: e.target.files[0] })
      setPreview(URL.createObjectURL(e.target.files[0]))
    }
  }
  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="name">Name</Label>

        <Input
          id="name"
          name="name"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.user.name}
          autoComplete="name"
          placeholder="Name"
          onChange={(e) =>
            setData("user", { ...data.user, name: e.target.value })
          }
        />

        <InputError message={(errors as any)?.name ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="email">Email</Label>

        <Input
          id="name"
          name="email"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.user.email}
          autoComplete="name"
          placeholder="Email"
          onChange={(e) =>
            setData("user", { ...data.user, email: e.target.value })
          }
        />

        <InputError message={(errors as any)?.email ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="account_uid">Account</Label>

        <Select
          value={data.user.account_uid}
          onValueChange={(val) =>
            setData("user", { ...data.user, account_uid: val })
          }
        >
          <SelectTrigger id="user_account_uid" className="w-full">
            <SelectValue placeholder="Select an account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((account) => (
              <SelectItem key={account.uid} value={account.uid}>
                {account.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InputError message={(errors as any)?.account ?? undefined} />
      </div>

      {mode === "edit" && (
        <div className="grid gap-2">
          <Label htmlFor="name">Status</Label>

          <Select
            value={data.user.state}
            onValueChange={(val) =>
              setData("user", { ...data.user, state: val })
            }
          >
            <SelectTrigger id="state" className="w-full">
              <SelectValue placeholder="Select a status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <InputError message={(errors as any)?.state ?? undefined} />
        </div>
      )}

      <ImageUpload
        label="Avatar"
        id="avatar"
        preview={preview ?? undefined}
        currentUrl={avatarUrl ?? undefined}
        onFileChange={handleFileChange}
        error={(errors as any)?.avatar ?? undefined}
        buttonLabel="Upload photo"
      />

      {mode === "create" && (
        <div className="grid gap-2">
          <Label>Notification</Label>
          <RadioGroup
            value={data.user.send_welcome_email}
            onValueChange={(val) =>
              setData("user", { ...data.user, send_welcome_email: val })
            }
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="1" id="send_welcome_email_yes" />
              <Label htmlFor="send_welcome_email_yes" className="font-normal">
                Send welcome email
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="0" id="send_welcome_email_no" />
              <Label htmlFor="send_welcome_email_no" className="font-normal">
                Don&apos;t send email
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}
    </>
  )
}
